// W24 Tabs (TabsMenu: router tabs, 15 px labels, the active one primary with legacy rad-tab-menu's 5 px underline, 40 px corners), W29 EmptyState
// (UiNoData), W31 settings scaffolding (UiCustomSettingWrapper: a 200 px heading column and the control column, rows
// split by a rule), the info banner (BaseAlert, used for "carries over to the subsequent day" and availability) and
// the loading state (q-spinner-gears).
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { CloudOff, Lock } from 'lucide-react';
// A q-tab's hover is Quasar's focus helper on the label colour (B995, review of #1989).
import { QUASAR_HOVER } from './button.tsx';

/**
 * Legacy UiSectionHeading's title: Quasar text-subtitle1 (16 px, 0.00937em) bold on the component's own 1.35rem
 * (21.6 px) line, in the primary ink. The one place this rule lives (B1329); the margins are each caller's, as legacy's
 * `additional-classes` differ page by page (the default q-mt-lg q-mb-sm, q-mt-xs q-mb-sm, q-mb-md, none).
 */
export const SECTION_HEADING = 'text-[16px] leading-[21.6px] font-bold tracking-[0.00937em] text-text-1';

export interface TabItem {
  readonly id: string;
  readonly label: string;
  readonly active: boolean;
}

interface TabBarProps {
  readonly label: string;
  readonly items: readonly TabItem[];
  /** Renders one tab as a link (the router's Link), given the classes legacy paints it with. */
  readonly renderTab: (item: TabItem, className: string) => ReactNode;
  /**
   * Legacy's plain `q-tabs dense narrow-indicator` (a customer's detail, B995) instead of TabsMenu: 36 px tabs, the
   * unselected labels in `text-grey` at Quasar's `.q-tab--inactive` opacity .85, a 2 px indicator under the label only,
   * and on hover Quasar's focus helper (the label colour darkened by black at .1, lightened by white at .4, shown at
   * 15 %), not a darker label. No arrows:
   * legacy draws its inside arrows only in a desktop browser whose tabs overflow, which for the customer's four tabs
   * (about 395 px) means a desktop window under about 430 px wide; left out for that rare case, which still scrolls.
   */
  readonly dense?: boolean;
  /**
   * Legacy's bare `q-tabs` (TabsMenuScroll: device settings, B1079): 48 px tabs padded 0 16 and at least 72 wide (the
   * 40 px `.q-tab__content` minimum), the unselected labels in
   * text-2 at Quasar's `.q-tab--inactive` opacity .85 with its focus-helper hover, Quasar's default indicator (2 px, square,
   * the whole tab wide), no rule under the bar and no margin after it. Overflow arrows as TabsMenu's. The tabs are buttons
   * that scroll one page (not links), so the strip is a `tablist`.
   */
  readonly plain?: boolean;
}


/** Material Icons' chevron_left and chevron_right (legacy q-tabs' arrows), in a 24 px box drawn at 32 px. */
const CHEVRON = { left: 'M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z', right: 'M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z' } as const;

/**
 * Legacy TabsMenu (q-tabs `outside-arrows mobile-arrows`): labels on one line; when the tabs overflow (B980: phones), the
 * bar pads 36 px each side, a 32 px chevron sits in each pad, faded (0.3, not clickable: QTabs.sass) at its end, and holding one scrolls the tabs
 * 5 px every 5 ms as Quasar's does; the active tab is scrolled into view. A wide bar draws neither arrow nor padding.
 *
 * Under 600 px of bar (Quasar's default `breakpoint`, measured on the bar, not the window: QTabs.js sets `justify` when its
 * width is under it), q-tabs justify: each tab grows (`flex: 1 1 auto`) and its label centres, the full indicator spanning
 * the grown tab and the dense narrow one staying label-wide under the label (B1023: Hardware at 390, a customer's tabs at
 * 620). Tabs that overflow have no room to grow, so they scroll as before. A container query follows the bar's own width.
 */
export function TabBar({ label, items, renderTab, dense = false, plain = false }: TabBarProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [edges, setEdges] = useState({ overflow: false, atStart: true, atEnd: true });
  const measure = useCallback(() => {
    const el = scroller.current;
    if (el === null) return;
    const overflow = !dense && el.scrollWidth > el.clientWidth + 1;
    const next = { overflow, atStart: el.scrollLeft <= 0, atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 };
    setEdges((was) => (was.overflow === next.overflow && was.atStart === next.atStart && was.atEnd === next.atEnd ? was : next));
  }, [dense]);
  const stop = useCallback(() => {
    if (timer.current !== null) clearInterval(timer.current);
    timer.current = null;
  }, []);
  const start = (direction: -1 | 1) => {
    stop();
    timer.current = setInterval(() => {
      const el = scroller.current;
      if (el === null) return;
      el.scrollLeft += direction * 5;
      measure();
      if (direction < 0 ? el.scrollLeft <= 0 : el.scrollLeft + el.clientWidth >= el.scrollWidth - 1) stop();
    }, 5);
  };
  useLayoutEffect(measure);
  // Quasar scrolls the active tab into view once the arrows' padding is in place (the scroller is 72 px narrower then), and
  // again only when the active tab changes (QTabs.js watches the model value), never on a re-render with fresh items.
  const activeId = items.find((item) => item.active)?.id;
  useLayoutEffect(() => {
    const el = scroller.current;
    const active = el?.querySelector<HTMLElement>('[data-tab-active]') ?? null;
    if (el === null || active === null || !edges.overflow) return;
    const box = el.getBoundingClientRect();
    const tab = active.getBoundingClientRect();
    const over = tab.right - box.right;
    const under = tab.left - box.left;
    if (over > 0) el.scrollLeft += over;
    else if (under < 0) el.scrollLeft += under;
    measure();
  }, [activeId, edges.overflow, measure]);
  useEffect(() => {
    const el = scroller.current;
    if (el === null) return undefined;
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      stop();
    };
  }, [measure, stop]);
  const arrow = (side: 'left' | 'right') => {
    const faded = side === 'left' ? edges.atStart : edges.atEnd;
    return (
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        data-testid={`tab-arrow-${side}`}
        onPointerDown={() => start(side === 'left' ? -1 : 1)}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        className={`absolute top-0 ${side === 'left' ? 'left-0' : 'right-0'} flex h-12 w-9 items-center justify-center text-text-2 ${faded ? 'pointer-events-none opacity-30' : 'cursor-pointer'}`}
      >
        <svg viewBox="0 0 24 24" width={32} height={32} fill="currentColor" aria-hidden><path d={CHEVRON[side]} /></svg>
      </button>
    );
  };
  return (
    <nav aria-label={label} className={`@container/tabs relative ${plain ? '' : 'mb-4 border-b border-line'} ${edges.overflow ? 'px-9' : ''}`} data-testid="tab-bar">
      {edges.overflow ? arrow('left') : null}
      <div ref={scroller} onScroll={measure} {...(plain ? { role: 'tablist', 'aria-label': label } : {})} className="flex gap-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="tab-scroller">
        {items.map((item) => (
          <div key={item.id} className="relative shrink-0 @max-[600px]/tabs:grow" {...(item.active ? { 'data-tab-active': '' } : {})}>
            {renderTab(
              item,
              // Quasar's .q-tab__label is on a 1.715em line (25.7 px at 15 px); a 1.5 line set the words a pixel low (B1491).
              // B2060: q-tab__content's min-width 40 and the 16 px padding keep every q-tab at least 72 wide, its label centred.
              `inline-flex ${dense ? 'h-9' : 'h-12'} min-w-[72px] items-center justify-center px-4 @max-[600px]/tabs:w-full text-[15px] leading-[1.715] font-medium whitespace-nowrap ${dense ? `${QUASAR_HOVER} ${item.active ? 'text-primary' : 'text-tab-quiet-ink opacity-85'}` : plain ? `${QUASAR_HOVER} ${item.active ? 'text-primary' : 'text-text-2 opacity-85'}` : item.active ? 'text-primary' : 'text-text-2 hover:text-text-1'}`,
            )}
            {!item.active ? null : dense
              // Quasar's narrow indicator is as wide as the label, under it: a 2 px slice of the label's own (unseen) text,
              // centred, so it follows the label whether the tab keeps its width or grows.
              ? <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center" data-testid="tab-indicator"><span className="h-[2px] overflow-hidden bg-primary text-[15px] font-medium whitespace-nowrap text-transparent select-none">{item.label}</span></span>
              : <span aria-hidden className={`absolute right-0 bottom-0 left-0 bg-primary ${plain ? 'h-[2px]' : 'h-[5px] rounded-t-[40px]'}`} data-testid="tab-indicator" />}
          </div>
        ))}
      </div>
      {edges.overflow ? arrow('right') : null}
    </nav>
  );
}

interface EmptyStateProps {
  readonly illustration?: string;
  readonly title: string;
  readonly description?: ReactNode;
  readonly testId?: string;
  /**
   * Under 600 px, legacy UiNoData's own phone rule (_ui.scss's xs `.ui-no-data-wrapper`): centred in 300 px, the line
   * wrapping at 320. Only for a port of a page whose legacy empty state is a UiNoData, checked beside it at 390; legacy's
   * other empty states (BaseTable's own no-data block, a plain q-pa-xl block, a table cell) keep 400 and 350 (B1087).
   * It also marks the title as UiNoData's label, whose wrapped lines start at the left, not centred (B1573).
   */
  readonly uiNoDataPhone?: boolean;
  /**
   * Legacy UiNoData's `hide-space` (its scoped `.no-height`, which outranks _ui.scss's 400 and the phone 300): the block
   * is only as tall as its picture and words, at every width. Legacy Schedule's empty week passes it (B1102).
   */
  readonly hideSpace?: boolean;
  /**
   * Legacy UiNoData's `icon-width="220px" icon-height="40%"` (the Gift Cards report's two tables, ReportsSalesGiftCards.vue
   * :692 and :776, its only callers): the picture is drawn contained in 220 px by 40% of the block, 160 px (120 in the phone
   * rule's 300 px block) (B1136).
   */
  readonly wideIllustration?: boolean;
  /** Legacy UiNoData's `icon-dark`: the picture drawn in dark instead of `illustration` (B1191). */
  readonly darkIllustration?: string;
}

/**
 * Legacy UiNoData, measured live on Timecards 2026-10-14: the 150 × 130 picture with `q-mb-sm` (8 px), the label as
 * text-subtitle1 (16 px on a 28 px line, medium) and the line straight under it (14 px on 21 px, no gap), centred in 400 px.
 */
export function EmptyState({ illustration, title, description, testId, uiNoDataPhone = false, hideSpace = false, wideIllustration = false, darkIllustration }: EmptyStateProps) {
  const height = hideSpace ? '' : ` min-h-[400px]${uiNoDataPhone ? ' max-[600px]:min-h-[300px]' : ''}`;
  // In dark, legacy draws `icon-dark` in the same box (UiNoData's isDarkMode swap), wide or not.
  const [width, pictureHeight, box] = wideIllustration
    ? [220, 160, ` h-[160px] w-[220px] object-contain${uiNoDataPhone ? ' max-[600px]:h-[120px]' : ''}`]
    : [150, 130, ''];
  const picture = darkIllustration === undefined
    ? <img src={illustration} alt="" width={width} height={pictureHeight} className={`mb-2${box}`} data-testid="empty-state-picture" />
    : (
      <>
        <img src={illustration} alt="" width={width} height={pictureHeight} className={`mb-2${box} dark:hidden`} data-testid="empty-state-picture" />
        <img src={darkIllustration} alt="" width={width} height={pictureHeight} className={`mb-2${box} hidden dark:block`} data-testid="empty-state-picture-dark" />
      </>
    );
  return (
    <div data-testid={testId} className={`flex${height} flex-col items-center justify-center text-center`}>
      {illustration === undefined ? null : picture}
      {/* Quasar's text-subtitle1 spaces its letters 0.00937em, not the body's 0.25 px, so POS Devices' title fits one line at 390 (B1121).
          In dark, legacy radish.scss draws `.rad-no-data-label` in --q-text-med-emph, white at 0.7, not the body's 0.87 (B1550).
          UiNoData's label has no text-align (only its line's wrapper is centred), so a title too long for one line fills the
          block and its lines start at the left edge, where a short one still sits centred as the column's item (B1573). */}
      <p className={`text-[16px] leading-7 font-medium tracking-[0.00937em] text-text-1 dark:text-white/70${uiNoDataPhone ? ' text-left' : ''}`} data-testid="empty-state-title">{title}</p>
      {/* A UiNoData port's plain line still goes through UiNoDataLine: legacy always wraps `subText` in its q-pr-xs span, so
          the line carries 4 px after its last word and a centred last line sits 2 px left of a bare string's (B1153). */}
      {description === undefined ? null : (
        <p className={`w-[350px] max-w-full${uiNoDataPhone ? ' max-[600px]:w-[320px]' : ''} text-[14px] leading-[21px] text-text-3`} data-testid="empty-state-line">
          {uiNoDataPhone && typeof description === 'string' ? <UiNoDataLine text={description} /> : description}
        </p>
      )}
    </div>
  );
}

/**
 * Legacy UiNoData's line (`.ui-no-data-content-wrapper`, B1121): `subText` with q-pr-xs (4 px after it), then the
 * `button-label` as an underlined primary link that never wraps inside itself (text-no-wrap) and does what the page's Add
 * button does, then `subTextOne` with q-pl-xs (4 px before it). Vue drops the whitespace between the spans, so the gaps
 * are only those paddings, and with the link a plain inline span (role button) there is no break opportunity between the
 * sub-text's last word and the link: a narrow line carries that word down with the link, as legacy's. An EmptyState's
 * `description`.
 */
export function UiNoDataLine({ text, link, after }: { readonly text: string; readonly link?: { readonly label: string; readonly onClick: () => void } | undefined; readonly after?: string | undefined }) {
  return (
    <>
      <span className="pr-1">{text}</span>
      {link === undefined ? null : (
        // A plain inline span, as legacy's: a <button> is an atomic inline box, which lets a line break fall just before it,
        // so Void Reasons broke "…BackOffice by / Adding…" where legacy keeps "by" with the link (review of #2153).
        <span
          role="button"
          tabIndex={0}
          className="cursor-pointer whitespace-nowrap text-primary underline"
          onClick={link.onClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              link.onClick();
            }
          }}
        >
          {link.label}
        </span>
      )}
      {after === undefined ? null : <span className="pl-1">{after}</span>}
    </>
  );
}

interface SettingRowProps {
  readonly heading: string;
  readonly children: ReactNode;
  readonly last?: boolean;
  /** The page's first row: no padding above it (legacy's settings stack starts flush; SettingsPage `top` sets the gap). */
  readonly first?: boolean;
  readonly testId?: string;
}

/**
 * UiCustomSettingWrapper: the bold heading column (15 %, 160–200 px) 48 px before the 85 % control column. It sits beside it
 * from Quasar's sm (600 px), not Tailwind's 640, and under 600 stacks 10 px above it; the heading keeps its 160 px
 * minimum when stacked, so a long one wraps as legacy's (B1035).
 */
export function SettingRow({ heading, children, last = false, first = false, testId }: SettingRowProps) {
  return (
    <section data-testid={testId} aria-label={heading} className={`flex flex-col gap-2.5 ${first ? 'pt-0 pb-6' : 'py-6'} min-[600px]:flex-row min-[600px]:gap-0 ${last ? '' : 'border-b border-line'}`}>
      {/* Legacy's widths as written: 15 % clamped to 160–200 beside an 85 % column, both shrinking as flex items (B1041). */}
      <h3 className="w-[15%] min-w-[160px] max-w-[200px] text-[14px] font-bold text-text-1 min-[600px]:mr-12">{heading}</h3>
      <div className="w-full min-w-0 min-[600px]:w-[85%]">{children}</div>
    </section>
  );
}

/**
 * The settings page body: legacy's ui-settings-block (940 px at most, 16 px padding each side, so a 908 px column
 * centred under the tabs when wide and 16 px gutters inside the page's when narrow: B805), with an optional intro line. `top` gives
 * legacy's 36 px from the tabs (16 px tab margin + 20) or the page top to a first row marked `first` (D1, 2026-10-13).
 */
export function SettingsPage({ intro, children, actions, top }: { readonly intro?: string; readonly children: ReactNode; readonly actions?: ReactNode; readonly top?: 'tabs' | 'page' }) {
  return (
    <div className={`mx-auto w-full max-w-[940px] px-4 pb-4 ${top === 'tabs' ? 'pt-5 max-[1024px]:pt-2' : top === 'page' ? 'pt-9 max-[1024px]:pt-6' : 'pt-6 max-[1024px]:pt-3'}`}>
      {/* Legacy's settings stack puts 24 px (row-gap) between the description and the first section. */}
      {intro === undefined ? null : <p className={`${top === undefined ? 'pb-2' : 'pb-6'} text-[14px] text-text-2`}>{intro}</p>}
      {children}
      {actions === undefined ? null : <div className="flex justify-end gap-3 pt-6">{actions}</div>}
    </div>
  );
}

/**
 * `error` is BaseAlert's `error-outline` (pp-alert-error-outline-soft: $redLight fill, negative edge and text, the
 * error_outline glyph), the refusals and validation alerts; `error-filled` is its `error` (pp-alert-error: filled negative,
 * white text and glyph), the Datacap key dialog's (B682).
 */
type BannerTone = 'info' | 'offline' | 'locked' | 'error' | 'error-filled' | 'warning' | 'warning-outline';

/** Material Icons `info`, `error_outline`, `error` and `warning`, which legacy BaseAlert's q-icon draws. */
const MATERIAL_ALERT: Readonly<Record<'info' | 'error' | 'error-filled' | 'warning' | 'warning-outline', string>> = {
  info: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z',
  // Material `error_outline`.
  error: 'M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z',
  // Material `error`.
  'error-filled': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z',
  // Material `warning` (BaseAlert `warning`, pp-alert-warning: the rate-limited device-claim refusal, B690).
  warning: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
  // The same Material `warning` (BaseAlert `warning` with `outline`, pp-alert-warning-outline: Review Time-Off Request's coverage note, B1567).
  'warning-outline': 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
};

/**
 * BaseAlert: the green info strip in dialogs, and the availability notices (needs internet, store offline, locked).
 * Legacy's geometry for every tone (B639 part 4; measured on GG LA at 1600 px on Departments and the Auto Discount
 * carry-over): `q-pa-sm` 8 px padding, radius .5em (7 px), 44 px for one line; the icon a 2em (28 px) Material glyph with
 * 8 px right padding and an 8 px gap before the text, top-aligned with it (`tw-items-start`). Info draws Material `info`
 * in primary on the info fill (#414241 in dark, B677) and error Material `error_outline` on $redLight (error-filled Material `error` on negative; both readable in dark, B691); offline and locked are Next's own and keep their glyphs in the same 28 px box.
 */
/**
 * `iconSize`: legacy BaseAlert's `icon-size` (2em, 28 px, unless the caller says; Employee Breaks' usage warning passes
 * 18px, B1571).
 */
export function Banner({ tone = 'info', children, testId, iconSize = 28 }: { readonly tone?: BannerTone; readonly children: ReactNode; readonly testId?: string; readonly iconSize?: number }) {
  const icon = tone === 'offline' ? <CloudOff size={iconSize} aria-hidden /> : tone === 'locked' ? <Lock size={iconSize} aria-hidden />
    : (
      <svg viewBox="0 0 24 24" width={iconSize} height={iconSize} aria-hidden data-testid="banner-glyph" className={tone === 'info' ? 'fill-primary' : 'fill-current'}>
        <path d={MATERIAL_ALERT[tone]} />
      </svg>
    );
  const palette = tone === 'info' ? 'bg-alert-info text-text-1' : tone === 'offline' ? 'bg-[var(--q-light-yellow)] text-text-1 border border-warning'
    : tone === 'error' ? 'border border-alert-error-soft-ink text-alert-error-soft-ink bg-alert-error-soft' : tone === 'error-filled' ? 'bg-negative text-on-negative'
    // Legacy's white on --q-warning is unreadable (1.6:1, B696); dark ink on its fill instead (B690).
    : tone === 'warning' ? 'bg-alert-warning text-on-warning'
    // Outlined: no fill, a 1 px --q-warning edge, and the text and glyph in --q-warning (darkened under the AA default, B1567).
    : tone === 'warning-outline' ? 'border border-warning text-alert-warning-outline-ink' : 'bg-gray-50 text-text-2';
  return (
    <div role={tone === 'offline' ? 'status' : tone === 'error' || tone === 'error-filled' || tone === 'warning' || tone === 'warning-outline' ? 'alert' : undefined} data-testid={testId} className={`flex items-start gap-2 rounded-[7px] p-2 text-[14px] leading-[21px] ${palette}`}>
      <span className={`flex ${iconSize === 28 ? 'size-7 ' : ''}shrink-0 box-content items-center justify-center pr-2`} style={iconSize === 28 ? undefined : { width: iconSize, height: iconSize }} data-banner-icon>{icon}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** "Change requires support 🔒" (LockedWrapper): an account-level field a merchant may only look at. */
export function LockedNote({ children }: { readonly children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-[12px] text-text-3">
      {children}
      <Lock size={12} aria-hidden />
    </span>
  );
}

/**
 * Legacy LockedWrapper with its lock shown (`show-lock-icon`): what a merchant sees of an account-level block, in a
 * gray-50 panel (12 px corners, 16 px padding) with the note and its 14 px lock at the top right, 8 px apart. As legacy,
 * the content takes the rest of the row (`flex: 1`) and the note wraps above it when the panel is narrow (`wrap-reverse`,
 * 24 px apart). `maxWidth`: legacy's `max-width` prop (450 px for Payment Configuration's program and rate). `noteInset`:
 * legacy's `tw-pt-[12px]` on the note, beside a radio group. No content: the note alone, at the right.
 */
export function LockedPanel({ note, children, maxWidth, noteInset = false, testId = 'locked-panel' }: {
  readonly note: ReactNode; readonly children?: ReactNode; readonly maxWidth?: number; readonly noteInset?: boolean; readonly testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={`flex flex-wrap-reverse items-end gap-6 rounded-[12px] bg-gray-50 p-4 ${children === undefined ? 'justify-end' : 'justify-between'}`}
      style={maxWidth === undefined ? undefined : { maxWidth }}
    >
      {/* Legacy's q-fields are unfilled in light mode, so the panel's grey shows through a locked field; dark mode fills
          them (_dark.scss), as Next's fields are (B1088). */}
      {children === undefined ? null : <div className="flex-1 [&_[data-field-box]]:bg-transparent dark:[&_[data-field-box]]:bg-field" data-testid="locked-content">{children}</div>}
      <span className={`inline-flex items-center gap-2${noteInset ? ' pt-3' : ''}`} data-testid="locked-note">
        <small className="text-[12px] leading-[normal] font-medium text-text-3">{note}</small>
        <Lock size={14} aria-hidden className="text-text-2" />
      </span>
    </div>
  );
}

/**
 * Legacy's TextLoader (components/loaders/TextLoader.vue, a q-skeleton of type text with Quasar's default wave): the box the
 * caller sizes, drawn at half its height about its middle (`scale(1, .5)`), in $separator-color (rgba(0,0,0,.12); dark
 * rgba(255,255,255,.05)) on a 4 px radius, with a light band sweeping across it (B1220: ReportsDate's 500 x 48 loader).
 */
export function TextSkeleton({ className, testId }: { readonly className: string; readonly testId?: string }) {
  return (
    <div aria-hidden data-testid={testId} className={`relative scale-y-50 cursor-wait overflow-hidden rounded-[4px] bg-black/[0.12] dark:bg-white/5 ${className}`}>
      <span className="absolute inset-0 bg-linear-to-r from-white/0 via-white/50 to-white/0 motion-safe:animate-skeleton-wave dark:via-white/10" />
    </div>
  );
}

export function LoadingState({ label = 'Loading' }: { readonly label?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex min-h-[240px] items-center justify-center">
      <span aria-hidden className="size-12 animate-spin rounded-full border-4 border-primary border-r-transparent" />
      <span className="sr-only">{label}</span>
    </div>
  );
}
